import React from "react";
import useSubscriptionLimits from "../../hooks/useSubscriptionLimits";

const allTemplates = [
  { id: 'default', name: 'Default Template', description: 'Modern colorful layout', planRequired: 'free' },
  { id: 'modern', name: 'Modern Template', description: 'Clean modern design with full features', planRequired: 'pro' },
  { id: 'formal', name: 'Formal Template', description: 'Professional business layout', planRequired: 'pro' }
];

function TemplateSelector({ 
  currentTemplate, 
  onTemplateChange, 
  isViewMode = false,
  pinnedKey = 'pinnedInvoiceTemplate'  // Default to invoice template for backward compatibility
}) {
  const { getAvailableTemplates, getCurrentPlan } = useSubscriptionLimits();
  

  const currentPlan = getCurrentPlan();
  const availableTemplateIds = getAvailableTemplates();
  const selectedTemplate = currentTemplate;

  // Show all templates for viewing, but restrict saving to available ones
  const availableTemplates = allTemplates;
  const currentIndex = availableTemplates.findIndex(t => t.id === currentTemplate);

  // Pin logic - use the provided pinnedKey prop
  const [pinnedTemplate, setPinnedTemplate] = React.useState(() => {
    return localStorage.getItem(pinnedKey) || '';
  });

  const handlePinTemplate = (templateId) => {
    localStorage.setItem(pinnedKey, templateId);
    setPinnedTemplate(templateId);
  };

  const handleUnpinTemplate = () => {
    localStorage.removeItem(pinnedKey);
    setPinnedTemplate('');
  };

  const goToPrevious = () => {
    if (availableTemplates.length <= 1) return;
    const newIndex = currentIndex > 0 ? currentIndex - 1 : availableTemplates.length - 1;
    const newTemplate = availableTemplates[newIndex];
    onTemplateChange(newTemplate.id);
  };

  const goToNext = () => {
    if (availableTemplates.length <= 1) return;
    const newIndex = currentIndex < availableTemplates.length - 1 ? currentIndex + 1 : 0;
    const newTemplate = availableTemplates[newIndex];
    onTemplateChange(newTemplate.id);
  };

  if (isViewMode) return null;

  return (
    <div className="flex items-center justify-center gap-4 p-3 bg-gray-50 rounded-lg border border-gray-200 mb-4 relative">
      {/* Pin Button - Top Right Corner */}
      {pinnedTemplate === availableTemplates[currentIndex]?.id ? (
        <button
          className="absolute top-2 right-2 p-1.5 rounded-full bg-yellow-500 text-white hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-400 shadow-sm"
          title="Unpin this template"
          onClick={handleUnpinTemplate}
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M16,12V4H17V2H7V4H8V12L6,14V16H11.2V22H12.8V16H18V14L16,12Z" />
          </svg>
        </button>
      ) : (
        <button
          className="absolute top-2 right-2 p-1.5 rounded-full bg-gray-200 text-gray-600 hover:bg-yellow-500 hover:text-white focus:outline-none focus:ring-2 focus:ring-yellow-400 shadow-sm transition-colors"
          title="Pin this template"
          onClick={() => handlePinTemplate(availableTemplates[currentIndex]?.id)}
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M16,12V4H17V2H7V4H8V12L6,14V16H11.2V22H12.8V16H18V14L16,12Z" />
          </svg>
        </button>
      )}

      {/* Left Arrow */}
      <button
        onClick={goToPrevious}
        disabled={availableTemplates.length <= 1}
        className="p-2 rounded-full bg-blue-500 text-white hover:bg-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        title="Previous template"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      {/* Template Info */}
      <div className="text-center">
        <div className="font-semibold text-gray-800">
          {availableTemplates[currentIndex]?.name || 'Default Template'}
        </div>
        <div className="text-sm text-gray-600">
          {availableTemplates[currentIndex]?.description || 'Modern colorful layout'}
        </div>
        <div className="text-xs text-gray-500 mt-1">
          {currentIndex + 1} of {availableTemplates.length}
        </div>
        {/* Plan-based template save restrictions */}
        {currentPlan.id === 'free' && selectedTemplate !== 'default' && (
          <div className="text-xs text-orange-600 mt-1 bg-orange-50 px-2 py-1 rounded">
            ⚠️ Preview only - Save requires Default template or upgrade
          </div>
        )}
        {currentPlan.id === 'free' && selectedTemplate === 'default' && (
          <div className="text-xs text-green-600 mt-1 bg-green-50 px-2 py-1 rounded">
            ✅ Template available for saving
          </div>
        )}
        {currentPlan.id === 'pro' && !availableTemplateIds.includes(selectedTemplate) && (
          <div className="text-xs text-orange-600 mt-1 bg-orange-50 px-2 py-1 rounded">
            ⚠️ Preview only - Save requires Business plan upgrade
          </div>
        )}
        {currentPlan.id === 'pro' && availableTemplateIds.includes(selectedTemplate) && (
          <div className="text-xs text-blue-600 mt-1 bg-blue-50 px-2 py-1 rounded">
            ✅ Template available for saving
          </div>
        )}
        {currentPlan.id === 'business' && (
          <div className="text-xs text-green-600 mt-1 bg-green-50 px-2 py-1 rounded">
            ✅ All templates available
          </div>
        )}
      </div>

      {/* Right Arrow */}
      <button
        onClick={goToNext}
        disabled={availableTemplates.length <= 1}
        className="p-2 rounded-full bg-blue-500 text-white hover:bg-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        title="Next template"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );
}

export default TemplateSelector; 